/** 플랫폼 관리자 화면에서 발급한 코드 목록. 운영 저장소는 Supabase(DB), localStorage는 이전·오프라인 폴백. */

import { planEndIsoFromStartMonthAndDurationMonths } from './academicYearFebruaryEnd';

const MAX_ROWS = 400;

/** 이벤트 코드 판매·사용 마감 여부(관리 화면용, 데모: localStorage) */
export type AdminEventPassSaleStatus = 'active' | 'closed';

/** 그룹 이용권 주문 상태(관리 화면용, 데모: localStorage) */
export type AdminGroupPassOrderStatus = 'issued' | 'refunded';

export interface AdminStoredEventPassRow {
  id: string;
  code: string;
  issuedAt: string;
  /** 이벤트 코드 이름(관리용) */
  eventName: string;
  /** 유료 플랜 시작 연·월 `YYYY-MM`(해당 월 1일) */
  paidPlanStartMonth: string;
  /** 이용 기간(개월) — 종료일 계산에 사용 */
  durationMonths: number;
  /** 이용권 종료일 `YYYY-MM-DD` (시작월 + 이용 기간 개월) */
  planEndIso: string;
  /** 활성 이용자 수(슬롯) */
  activeUserCount: number;
  /** 코드를 실제로 활성화(등록)한 이용자 수 — 데모: 관리 화면에서만 조정·표시, 상한은 activeUserCount */
  activatedUserCount: number;
  /** 활성(신규 사용 가능) / 마감(관리자가 중단) */
  saleStatus: AdminEventPassSaleStatus;
}

export interface AdminStoredGroupPassRow {
  id: string;
  code: string;
  issuedAt: string;
  groupName: string;
  contactEmail: string;
  /** 유료 플랜 시작일 `YYYY-MM-DD` */
  paidPlanStartDate: string;
  /** 그룹에 허용되는 이용자 수 */
  userCount: number;
  /** 코드를 실제로 활성화한 이용자 수 — 데모: 상한은 userCount */
  activatedUserCount: number;
  /** 발행(유효) / 환불 완료 */
  orderStatus: AdminGroupPassOrderStatus;
  /** 환불 처리 시각(ISO) */
  refundedAt?: string | null;
  /** 코드 재발송(데모 기록) 시각(ISO) */
  lastResentAt?: string | null;
}

function evtStorageKey(workspaceId: string): string {
  return `cp_admin_pass_evt_v1:${workspaceId.trim()}`;
}

function grpStorageKey(workspaceId: string): string {
  return `cp_admin_pass_grp_v1:${workspaceId.trim()}`;
}

export function newAdminIssuedPassRowId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `r_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function parseRows<T>(raw: string | null): T[] {
  if (!raw) return [];
  try {
    const j = JSON.parse(raw) as unknown;
    if (!Array.isArray(j)) return [];
    return j as T[];
  } catch {
    return [];
  }
}

function migrateEventRow(o: Record<string, unknown>): AdminStoredEventPassRow | null {
  if (typeof o.id !== 'string' || typeof o.code !== 'string' || typeof o.issuedAt !== 'string') return null;
  const legacyMemo = typeof o.memo === 'string' ? o.memo.trim() : '';
  const eventName =
    typeof o.eventName === 'string' && o.eventName.trim() ? o.eventName.trim() : legacyMemo || '(이름 없음)';
  let paidPlanStartMonth = typeof o.paidPlanStartMonth === 'string' ? o.paidPlanStartMonth.trim() : '';
  if (!/^\d{4}-\d{2}$/.test(paidPlanStartMonth)) {
    paidPlanStartMonth = new Date().toISOString().slice(0, 7);
  }
  const durationRaw = o.durationMonths;
  const durationMonths =
    typeof durationRaw === 'number' && Number.isFinite(durationRaw) && durationRaw > 0
      ? Math.min(36, Math.floor(durationRaw))
      : 1;
  const recomputed = planEndIsoFromStartMonthAndDurationMonths(paidPlanStartMonth, durationMonths);
  let planEndIso = typeof o.planEndIso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.planEndIso.trim())
    ? o.planEndIso.trim()
    : '';
  if (!planEndIso && recomputed) planEndIso = recomputed;
  if (!planEndIso) {
    const legacyFeb =
      typeof o.planEndFebruaryIso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.planEndFebruaryIso.trim())
        ? o.planEndFebruaryIso.trim()
        : '';
    planEndIso = legacyFeb || planEndIsoFromStartMonthAndDurationMonths(paidPlanStartMonth, 1) || '';
  }
  const activeRaw = o.activeUserCount;
  const activeUserCount =
    typeof activeRaw === 'number' && Number.isFinite(activeRaw) && activeRaw > 0
      ? Math.min(999, Math.floor(activeRaw))
      : 1;
  const actRaw = o.activatedUserCount;
  let activatedUserCount =
    typeof actRaw === 'number' && Number.isFinite(actRaw) && actRaw >= 0 ? Math.floor(actRaw) : 0;
  activatedUserCount = Math.min(Math.max(0, activatedUserCount), activeUserCount);
  const saleStatus: AdminEventPassSaleStatus = o.saleStatus === 'closed' ? 'closed' : 'active';
  return {
    id: o.id,
    code: o.code,
    issuedAt: o.issuedAt,
    eventName,
    paidPlanStartMonth,
    durationMonths,
    planEndIso,
    activeUserCount,
    activatedUserCount,
    saleStatus,
  };
}

function migrateGroupRow(o: Record<string, unknown>): AdminStoredGroupPassRow | null {
  if (typeof o.id !== 'string' || typeof o.code !== 'string' || typeof o.issuedAt !== 'string') return null;
  const legacyMemo = typeof o.memo === 'string' ? o.memo.trim() : '';
  const groupName =
    typeof o.groupName === 'string' && o.groupName.trim() ? o.groupName.trim() : legacyMemo || '(이름 없음)';
  let paidPlanStartDate = typeof o.paidPlanStartDate === 'string' ? o.paidPlanStartDate.trim() : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(paidPlanStartDate)) {
    paidPlanStartDate = new Date().toISOString().slice(0, 10);
  }
  const userCount =
    typeof o.userCount === 'number' && Number.isFinite(o.userCount) && o.userCount > 0
      ? Math.min(999, Math.floor(o.userCount))
      : typeof o.quantity === 'number' && Number.isFinite(o.quantity) && o.quantity > 0
        ? Math.min(999, Math.floor(o.quantity))
        : 1;
  const contactEmail = typeof o.contactEmail === 'string' ? o.contactEmail.trim() : '';
  const actGr = o.activatedUserCount;
  let activatedUserCount =
    typeof actGr === 'number' && Number.isFinite(actGr) && actGr >= 0 ? Math.floor(actGr) : 0;
  activatedUserCount = Math.min(Math.max(0, activatedUserCount), userCount);
  const orderStatus: AdminGroupPassOrderStatus = o.orderStatus === 'refunded' ? 'refunded' : 'issued';
  let refundedAt: string | null = null;
  if (orderStatus === 'refunded') {
    const rawRef = typeof o.refundedAt === 'string' ? o.refundedAt.trim() : '';
    refundedAt =
      rawRef ||
      (typeof o.issuedAt === 'string' && o.issuedAt ? o.issuedAt : new Date().toISOString());
  }
  const lastResentAt =
    typeof o.lastResentAt === 'string' && o.lastResentAt.trim() ? o.lastResentAt.trim() : null;
  return {
    id: o.id,
    code: o.code,
    issuedAt: o.issuedAt,
    groupName,
    contactEmail,
    paidPlanStartDate,
    userCount,
    activatedUserCount,
    orderStatus,
    refundedAt,
    lastResentAt,
  };
}

export function loadEventIssuances(workspaceId: string): AdminStoredEventPassRow[] {
  if (typeof window === 'undefined') return [];
  const raw = parseRows<Record<string, unknown>>(window.localStorage.getItem(evtStorageKey(workspaceId)));
  return raw.map(migrateEventRow).filter((x): x is AdminStoredEventPassRow => x !== null).slice(0, MAX_ROWS);
}

export function loadGroupIssuances(workspaceId: string): AdminStoredGroupPassRow[] {
  if (typeof window === 'undefined') return [];
  const raw = parseRows<Record<string, unknown>>(window.localStorage.getItem(grpStorageKey(workspaceId)));
  return raw.map(migrateGroupRow).filter((x): x is AdminStoredGroupPassRow => x !== null).slice(0, MAX_ROWS);
}

export function saveEventIssuances(workspaceId: string, rows: AdminStoredEventPassRow[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(evtStorageKey(workspaceId), JSON.stringify(rows.slice(0, MAX_ROWS)));
}

export function saveGroupIssuances(workspaceId: string, rows: AdminStoredGroupPassRow[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(grpStorageKey(workspaceId), JSON.stringify(rows.slice(0, MAX_ROWS)));
}

export function prependEventIssuances(workspaceId: string, newRows: AdminStoredEventPassRow[]): void {
  const cur = loadEventIssuances(workspaceId);
  saveEventIssuances(workspaceId, [...newRows, ...cur]);
}

export function prependGroupIssuances(workspaceId: string, newRows: AdminStoredGroupPassRow[]): void {
  const cur = loadGroupIssuances(workspaceId);
  saveGroupIssuances(workspaceId, [...newRows, ...cur]);
}

export function deleteEventIssuanceRow(workspaceId: string, id: string): void {
  saveEventIssuances(
    workspaceId,
    loadEventIssuances(workspaceId).filter((r) => r.id !== id),
  );
}

export function patchEventIssuanceRow(
  workspaceId: string,
  id: string,
  patch: Partial<Omit<AdminStoredEventPassRow, 'id'>>,
): void {
  saveEventIssuances(
    workspaceId,
    loadEventIssuances(workspaceId).map((r) => (r.id === id ? { ...r, ...patch } : r)),
  );
}

export function patchGroupIssuanceRow(
  workspaceId: string,
  id: string,
  patch: Partial<Omit<AdminStoredGroupPassRow, 'id'>>,
): void {
  saveGroupIssuances(
    workspaceId,
    loadGroupIssuances(workspaceId).map((r) => (r.id === id ? { ...r, ...patch } : r)),
  );
}
